import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { projectsAPI } from '../services/api';
import { Building2, ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const ProjectForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDeveloper } = useAuth();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    start_date: '',
    end_date: '',
    budget: '',
    direct_cost: '',
    sale_cost: '',
    status: 'activo'
  });

  useEffect(() => {
    if (!isDeveloper()) {
      navigate('/projects');
      return;
    }
    
    if (isEdit) {
      fetchProject();
    }
  }, [id, isDeveloper]);

  const fetchProject = async () => {
    setLoading(true);
    try {
      const response = await projectsAPI.getById(id);
      const project = response?.data?.data || response?.data;
      setFormData({
        name: project?.name || '',
        description: project?.description || '',
        location: project?.location || '',
        start_date: project?.start_date || '',
        end_date: project?.end_date || '',
        budget: project?.budget?.toString() || '',
        direct_cost: project?.direct_cost?.toString() || '',
        sale_cost: project?.sale_cost?.toString() || '',
        status: project?.status || 'activo'
      });
    } catch (error) {
      console.error('Error al cargar proyecto:', error);
      toast.error('Error al cargar proyecto');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre de la obra es requerido');
      return;
    }

    setSaving(true);
    
    try {
      const data = {
        ...formData,
        budget: parseFloat(formData.budget) || 0,
        direct_cost: parseFloat(formData.direct_cost) || 0,
        sale_cost: parseFloat(formData.sale_cost) || 0
      };

      if (isEdit) {
        await projectsAPI.update(id, data);
        toast.success('Obra actualizada');
      } else {
        const response = await projectsAPI.create(data);
        const project = response?.data?.data || response?.data;
        toast.success('Obra creada');
        navigate(`/projects/${project.id}`);
        return;
      }
      
      navigate(`/projects/${id}`);
    } catch (error) {
      toast.error(isEdit ? 'Error al actualizar obra' : 'Error al crear obra');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#C05621]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="project-form">
        <Link to="/projects" className="inline-flex items-center gap-2 text-[#5C554B] hover:text-[#C05621] transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Volver a obras
        </Link>

        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-[#C05621]/10 rounded-xl flex items-center justify-center">
            <Building2 className="w-7 h-7 text-[#C05621]" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#2D2A26]">
              {isEdit ? 'Editar Obra' : 'Nueva Obra'}
            </h1>
            <p className="text-[#5C554B]">
              {isEdit ? 'Modifica los datos de la obra' : 'Completa los datos para crear una nueva obra'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle>Información General</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="name">Nombre de la obra *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ej: Torre Residencial Norte"
                    className="input-default"
                    required
                    data-testid="project-name-input"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Descripción del proyecto..."
                    className="min-h-24 bg-white border-[#E2E8F0] focus:ring-2 focus:ring-[#C05621]/20 focus:border-[#C05621]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Ubicación</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Ciudad, Estado"
                    className="input-default"
                  />
                </div>

                {isEdit && (
                  <div className="space-y-2">
                    <Label htmlFor="status">Estado</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                      <SelectTrigger className="h-12 bg-white border-[#E2E8F0]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="activo">Activo</SelectItem>
                        <SelectItem value="pausado">Pausado</SelectItem>
                        <SelectItem value="completado">Completado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="start_date">Fecha de inicio</Label>
                  <Input
                    id="start_date"
                    name="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={handleChange}
                    className="input-default"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="end_date">Fecha de fin</Label>
                  <Input
                    id="end_date"
                    name="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={handleChange}
                    className="input-default"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard mt-6">
            <CardHeader>
              <CardTitle>Información Financiera</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="budget">Presupuesto Total</Label>
                  <Input
                    id="budget"
                    name="budget"
                    type="number"
                    step="0.01"
                    value={formData.budget}
                    onChange={handleChange}
                    placeholder="0.00"
                    className="input-default"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="direct_cost">Costo Directo</Label>
                  <Input
                    id="direct_cost"
                    name="direct_cost"
                    type="number"
                    step="0.01"
                    value={formData.direct_cost}
                    onChange={handleChange}
                    placeholder="0.00"
                    className="input-default"
                  />
                  <p className="text-xs text-[#9CA3AF]">Solo visible para desarrolladores</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sale_cost">Costo de Venta</Label>
                  <Input
                    id="sale_cost"
                    name="sale_cost"
                    type="number"
                    step="0.01"
                    value={formData.sale_cost}
                    onChange={handleChange}
                    placeholder="0.00"
                    className="input-default"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4 mt-6">
            <Link to="/projects">
              <Button type="button" variant="outline" className="btn-secondary">
                Cancelar
              </Button>
            </Link>
            <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-project-btn">
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  {isEdit ? 'Guardar cambios' : 'Crear obra'}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default ProjectForm;
